import { FC, ReactElement, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../core/redux/hooks'
import { getRoleTypeByString } from '../commons/utils/utils'
import { Box, Divider, Grid, InputLabel } from '@mui/material'
import { constants } from '../commons/constants/constants'
import SelfServeButton from '../ui/button/Button'
import { t } from 'i18next'
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined'
import { RoutePath } from '../core/routes/routePaths'
import { userHasTenantPermission } from '../core/routes/router'
import LiveOffer from '../offers/live-offer/LiveOffer'
import { getOfferCategoryByID, getLiveOffers } from './slices'
import {
  setBackButtonRoute,
  setBackButtonTitle,
  setEntity
} from '../ui/sidebar/slices'
import NoLiveOffer from '../offers/live-offer/NoLiveOffer'
import { OfferCategoriesParams } from './interfaces/offer-categories'
import SelfServeROTextField from '../ui/text-field/TextFieldRO'

const OfferCategoriesDetails: FC<{}> = (): ReactElement | null => {
  const { offerCategorySelected, liveOffers, loading } = useAppSelector(
    (state) => state.offerCategories
  )
  const { userType, tenantID, agencyID, isAgencyEnabled } = useAppSelector(
    (state) => state.auth
  )
  const role = getRoleTypeByString(userType)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()

  const categoryParams: OfferCategoriesParams = useMemo(() => {
    return {
      id: id ?? '',
      userType,
      agencyID,
      tenantID
    }
  }, [agencyID, id, tenantID, userType])

  useEffect(() => {
    if (id != null) {
      void dispatch(getOfferCategoryByID(categoryParams))
      void dispatch(getLiveOffers(categoryParams))
      dispatch(setEntity(constants.SIDEBAR_ENTITY_TYPES.OFFER_CATEGORY))
      dispatch(setBackButtonTitle(t`BackToCategories`))
      dispatch(setBackButtonRoute(RoutePath.OfferCategories))
    }

    return () => {
      dispatch(setEntity(constants.SIDEBAR_ENTITY_TYPES.GENERAL))
    }
  }, [id, dispatch, categoryParams])

  return offerCategorySelected.OfferCategoryID != null && !loading
    ? (
      <Box>
        <Box maxWidth={constants.MAX_WIDTH_CARD}>
          <Box className='flex-row'>
            <Box>
              <h1 className='title'>{t`OfferCategoryDetails`}</h1>
            </Box>
            <Box marginLeft='2em' marginTop='.3em'>
              <SelfServeButton
                dataTestId='editButton'
                size='small'
                id='edit-category-button'
                variant='contained'
                color='secondary'
                onClick={() => navigate(RoutePath.Edit)}
              >
                <ModeEditOutlinedIcon /> &nbsp; {t`Edit`}
              </SelfServeButton>
            </Box>
          </Box>
          <Box className='box-details'>
            {userHasTenantPermission(role) && isAgencyEnabled && (
              <SelfServeROTextField
                id='agency-name'
                label={t`AgencyName`}
                defaultValue={offerCategorySelected.AgencyName}
                showDivider
              />
            )}
            <SelfServeROTextField
              id='category-name'
              label={t`CategoryName`}
              defaultValue={offerCategorySelected.CategoryName}
              showDivider
              classNameDivider='mt-0'
            />
            <InputLabel size='small' variant='standard' className='small-label'>
              {t`CategoryIcon`}
            </InputLabel>
            <img
              src={offerCategorySelected.IconImageUrl}
              alt='category-icon'
              className='mt-2 square-image-list'
            />
            <Divider className='divider' />
            <SelfServeROTextField
              id='ui-position'
              label={t`UiPosition`}
              defaultValue={offerCategorySelected.WebUiPosition}
              showDivider
              classNameDivider='mt-0'
            />
          </Box>
          <InputLabel
            size='small'
            variant='standard'
            className='small-label live-offer-text'
          >
            {t`LiveOffers`}
          </InputLabel>
        </Box>
        <Box maxWidth='55em'>
          {liveOffers.length > 0
            ? (
              <Grid
                container
                direction='row'
                className='live-offer-grid'
                wrap='nowrap'
              >
                {liveOffers.map((offer) => {
                  return (
                    <Grid item key={offer.OfferID}>
                      <LiveOffer offer={offer} />
                    </Grid>
                  )
                })}
              </Grid>
              )
            : (
              <NoLiveOffer />
              )}
        </Box>
      </Box>
      )
    : null
}

export default OfferCategoriesDetails
